import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { BaseGetByFileIdRequest } from '@app-types/base/base';
import { MatDialog } from '@angular/material/dialog';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SearchBarComponent } from '@app-components/common/search-bar/search-bar.component';
import { NoDataComponent } from '@app-components/common/no-data/no-data.component';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import {
  MAIL_ACCOUNT_ID,
  SETTING_SETTINGS,
  defaultFilter,
} from '@app-shared/constants/constants';
import { DrawerService } from '@app-services/drawer.service';
import { DeleteDialogComponent } from '@app-components/common/delete-dialog/delete-dialog.component';
import { Subscription } from 'rxjs';
import { SkeletonTableComponent } from '@app-components/common/skeletons/skeleton-table/skeleton-table.component';
import { InfinityScrollComponent } from '@app-components/common/infinity-scroll/infinity-scroll.component';
import { TableHeaderComponent } from '@app-components/common/table-header/table-header.component';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { OptionsEnum } from '@app-types/common';
import { OrderDirection } from '@app-types/enums/order-direction';
import { LoaderComponent } from '@app-components/common/loader/loader.component';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';
import { ContactInfo, FilterContacts } from '@app-types/api/contact';
import { TagContract } from '@app-types/api/tag';
import { AddTagsDialogComponent } from '@app-components/common/add-tags-dialog/add-tags-dialog.component';
import { ContactsSearchOrder } from '@app-types/enums/contacts';
import { FormatContactsSearchOrders } from '@app-pipes/format-contacts-search-orders.pipe';
import { MatCheckbox } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { NgTemplateOutlet } from '@angular/common';
import { ContactDialogComponent } from '@app-components/contact-dialog/contact-dialog.component';
import { ContactClient } from '@app-services/api/clients/contact.client';
import { TagType } from '@app-types/enums/tags';
import { TagClient } from '@app-services/api/clients/tag.client';
import { ConfirmCloseDialogComponent } from '@app-components/common/confirm-close-dialog/confirm-close-dialog.component';
import { AddressBookFilterDrawerComponent } from '@app-components/address-book-filter-drawer/address-book-filter-drawer.component';
import { ImportContactsComponent } from '@app-components/import-contacts/import-contacts.component';
import { ErrorCode } from '@app-types/enums/error-code';
import { TagComponentComponent } from '@app-components/common/tag-component/tag-component.component';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { EmailsPageStateService } from '@app-services/emails/emails-page-state.service';
import { ROUTES } from '@app-shared/constants/routes/fe.routes';
import { EMAIL_QUERY_PARAM } from '@app-shared/constants/constants';
import { encodeInfoMailAccount } from '@app-utils/search-params';
import { getFolderByType } from '@app-utils/folders';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { PermissionType } from '@app-types/enums/permission-type';
import { PermissionService } from '@app-services/permission/permission.service';

const { STEP_FETCH } = SETTING_SETTINGS;
const { EMAILS } = ROUTES;

@Component({
  selector: 'app-mail-account-address-book',
  templateUrl: './mail-account-address-book.component.html',
  styleUrls: ['./mail-account-address-book.component.scss'],
  imports: [
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    SearchBarComponent,
    NoDataComponent,
    SomethingWentWrongComponent,
    MatTableModule,
    MatTooltipModule,
    MatCardModule,
    SkeletonTableComponent,
    TableHeaderComponent,
    InfinityScrollComponent,
    MatSort,
    MatSortModule,
    LoaderComponent,
    MatCheckbox,
    NgTemplateOutlet,
    AddTagsDialogComponent,
    ContactDialogComponent,
    TagComponentComponent,
    ImportContactsComponent,
    AddressBookFilterDrawerComponent,
  ],
})
export class MailAccountAddressBookComponent implements OnInit, OnDestroy {
  @HostBinding('class') className = 'setting-container';
  public mailAccountId: number | null;
  public isLoading = true;
  public isError = false;
  public dataSource = new MatTableDataSource<ContactInfo>([]);
  public displayedColumns: string[] = [
    'select',
    'alias',
    'designation',
    'name',
    'lastName',
    'mailAccount',
    'label',
    'actions',
  ];
  public searchValue = '';
  public canNavigateForward = false;
  public isLoadingScroll = false;
  public isErrorScroll = false;
  public offset = 0;
  public stepFetch = STEP_FETCH;
  public totalSize = 0;
  public useMobileView = false;
  private useMobileViewSubscription: Subscription;

  public editingContact: number | null = null;
  public contactFormDirty = false;
  public searchOrder: ContactsSearchOrder | 0 = 0;
  public searchOrders: OptionsEnum[];
  public filter: FilterContacts = {
    ...defaultFilter,
    tags: [],
  };
  public isOpenFilters = false;
  public isExporting = false;
  public isExportingVcCards = false;
  private formatContactsSearchOrders = new FormatContactsSearchOrders();
  isReloadLoading = false;
  private suppressSortDrivenSearch = false;

  public selection = new SelectionModel<ContactInfo>(true, []);
  public taggingContacts: ContactInfo[] | null = null;
  canManageMailAccountContactsImportExport = false;
  canManageMailAccountContactsEditDelete = false;

  @ViewChild('addDrawer') addDrawer: TemplateRef<any>;
  @ViewChild('uploadContactsDrawer') uploadContactsDrawer: TemplateRef<any>;
  @ViewChild('tagsDrawer') tagsDrawer: TemplateRef<unknown>;
  @ViewChild('filterTagsDrawer') filterTagsDrawer: TemplateRef<unknown>;
  @ViewChild(InfinityScrollComponent) infinityScroll: InfinityScrollComponent;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public matchError: MatchError,
    protected router: Router,
    protected route: ActivatedRoute,
    private contactClient: ContactClient,
    public dialog: MatDialog,
    private mobileObserverService: MobileObserverService,
    private drawerService: DrawerService,
    private tempFileClient: TempFileClient,
    private tagClient: TagClient,
    private userWorkspaceService: UserWorkspaceService,
    private emailsPageStateService: EmailsPageStateService,
    private permissionService: PermissionService
  ) {}

  async ngOnInit(): Promise<void> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.useMobileView = isMobile));

    this.searchOrders = this.displayedColumns
      .filter(el => el !== 'actions')
      .map(key => ({
        id: this.formatContactsSearchOrders.transform(key),
        name: key,
      }));

    const mailAccId = this.route.snapshot.paramMap.get(MAIL_ACCOUNT_ID);
    this.mailAccountId = mailAccId ? +mailAccId : null;
    await this.loadCanManageMailAccountContactsImportExport();
    await this.loadContactList(true);
  }

  private async loadCanManageMailAccountContactsImportExport(): Promise<void> {
    if (!this.mailAccountId) return;
    this.canManageMailAccountContactsImportExport =
      await this.permissionService.hasPermissionOver(
        null,
        this.mailAccountId,
        PermissionType.CanManageMailAccountContactsImportExport
      );

    this.canManageMailAccountContactsEditDelete =
      await this.permissionService.hasPermissionOver(
        null,
        this.mailAccountId,
        PermissionType.CanManageMailAccountContacts
      );
  }

  async loadContactList(fetchCounter: boolean): Promise<void> {
    if (!this.mailAccountId) {
      this.isLoading = false;
      return;
    }

    this.canNavigateForward = false;
    const loaderTimeout =
      !this.offset && !this.dataSource.data.length
        ? setTimeout(() => {
            this.isLoading = true;
          }, 500)
        : undefined;

    this.offset && (this.isLoadingScroll = true);
    this.isError = false;
    this.isErrorScroll = false;

    try {
      const trimmed = this.searchValue?.trim();
      const nullableSearchValue = trimmed ? trimmed : null;
      const { tags: _, ...rest } = this.filter;
      const filter = {
        ...rest,
        tagIds: this.filter?.tags?.length
          ? this.filter.tags.map(el => el.tagId)
          : [],
      };

      const request = {
        mailAccountId: this.mailAccountId,
        offset: this.offset,
        fetch: this.stepFetch,
        filter,
        search: nullableSearchValue,
      };
      const response = await this.contactClient.searchForAccount(request);

      const batch = response.data.map(el => ({
        ...el,
        defaultEmail: el.emails.find(email => email.isDefault)?.email ?? '',
      }));
      this.dataSource.data =
        this.offset === 0 ? batch : [...this.dataSource.data, ...batch];

      if (this.offset === 0) {
        const selectedContactIds = new Set(
          this.selection.selected
            .map(c => c.contactId)
            .filter((id): id is number => id != null)
        );
        this.selection.clear();
        if (selectedContactIds.size > 0) {
          for (const row of this.dataSource.data) {
            if (row.contactId && selectedContactIds.has(row.contactId)) {
              this.selection.select(row);
            }
          }
        }
      }

      if (fetchCounter) {
        const counterResponse = await this.contactClient.getSearchCounter({
          mailAccountId: this.mailAccountId,
          filter,
          search: nullableSearchValue,
        });
        this.totalSize = counterResponse.result;
      }

      this.canNavigateForward =
        this.totalSize > 0
          ? this.dataSource.data.length < this.totalSize
          : batch.length === this.stepFetch;
    } catch (e) {
      if (this.isLoadingScroll) {
        this.isErrorScroll = true;
        this.matchError.logError(e);
      } else {
        this.isError = true;
        this.matchError.logError(e);
      }
    } finally {
      loaderTimeout && clearTimeout(loaderTimeout);
      this.isLoading && (this.isLoading = false);
      this.isLoadingScroll && (this.isLoadingScroll = false);
    }
  }

  public onSearchValueChanged(newSearchValue: string): void {
    this.searchValue = newSearchValue;
  }

  public async onContactSubmit(): Promise<void> {
    this.contactFormDirty = false;
    await this.onSearch();
  }

  public onCreateNewContact(): void {
    this.editingContact = null;
    this.drawerService.openDrawer(
      this.addDrawer,
      () => {
        this.onCloseEditModalForm();
      },
      'create-email'
    );
  }

  private getContactRecipientEmail(contact: ContactInfo): string | null {
    const email =
      contact.emails?.find(e => e.isDefault)?.email?.trim() ||
      contact.emails?.[0]?.email?.trim();
    return email || null;
  }

  sendEmailToContacts(contacts: ContactInfo[]): void {
    if (!this.mailAccountId || !contacts.length) {
      return;
    }

    const recipients = [
      ...new Set(
        contacts
          .map(contact => this.getContactRecipientEmail(contact))
          .filter((email): email is string => !!email)
      ),
    ];

    if (!recipients.length) {
      this.matchError.openErrorDialog(
        'errorPopupTitle',
        'contactsNoEmailForSend'
      );
      return;
    }

    this.emailsPageStateService.infoInitialContactsTo = {
      mailAccountId: this.mailAccountId,
      to: recipients,
    };
    const folderId =
      getFolderByType(
        this.userWorkspaceService.userFolders,
        this.mailAccountId,
        MailFolderType.Inbox
      ) ??
      getFolderByType(
        this.userWorkspaceService.userFolders,
        this.mailAccountId,
        MailFolderType.Draft
      );

    if (!folderId) {
      void this.router.navigate([EMAILS]);
      return;
    }

    const folderType =
      this.userWorkspaceService.userFolders.find(
        f => f.mailFolderId === folderId
      )?.folderType ?? MailFolderType.Inbox;

    const infoMailAcc = encodeInfoMailAccount(
      this.mailAccountId,
      folderId,
      folderType
    );

    void this.router.navigate([EMAILS], {
      queryParams: {
        [EMAIL_QUERY_PARAM]: infoMailAcc,
      },
    });
  }

  openTagsForContact(contact?: ContactInfo): void {
    if (!this.mailAccountId) return;
    this.taggingContacts = contact ? [contact] : [...this.selection.selected];
    this.drawerService.openDrawer(this.tagsDrawer, undefined, 'small');
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach(row => this.selection.select(row));
  }

  public onEditContactBtnClicked(contact: ContactInfo): void {
    if (!contact.contactId) return;
    this.editingContact = contact.contactId;
    this.drawerService.openDrawer(
      this.addDrawer,
      () => {
        this.onCloseEditModalForm();
      },
      'create-email'
    );
  }

  onCloseEditModalForm(): void {
    if (this.contactFormDirty) {
      const dialogRef = this.dialog.open(ConfirmCloseDialogComponent, {
        autoFocus: false,
        data: {
          title: 'undoChanges',
          text: 'undoChangesText',
          secondText: 'wantToProceed',
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result?.isClose) {
          this.contactFormDirty = false;
          this.drawerService.closeDrawer();
          this.editingContact = null;
        }
      });
      return;
    }
    this.editingContact = null;
    this.drawerService.closeDrawer();
  }

  async onDeleteBtnClicked(contactId?: number | null): Promise<void> {
    if (!this.mailAccountId) return;
    await this.contactClient.delete({
      mailAccountId: this.mailAccountId,
      list: contactId
        ? [contactId]
        : this.selection.selected
            .map(el => el.contactId)
            .filter((id): id is number => id != null),
    });
  }

  public deleteContact(contact?: ContactInfo): void {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '450px',
      autoFocus: false,
      data: {
        title: 'confirmation',
        subTitles: [
          {
            title: 'deleteContactSubtitle',
            subTitle:
              contact?.alias ||
              contact?.companyName ||
              contact?.firstName ||
              '',
          },
        ],
        onDelete: async () => {
          await this.onDeleteBtnClicked(contact?.contactId);
          !contact?.contactId && this.selection.clear();
        },
      },
    });

    dialogRef.afterClosed().subscribe(async x => {
      if (x?.isDeleted) {
        this.onSearch();
      }
    });
  }

  public async onScroll(): Promise<void> {
    this.offset = this.offset + this.stepFetch;
    await this.loadContactList(false);
  }

  async refreshData(): Promise<void> {
    await this.loadContactList(false);
  }

  resetScrollData(): void {
    this.offset = 0;
  }

  async reload(): Promise<void> {
    this.isReloadLoading = true;
    this.selection.clear();
    this.filter = { ...defaultFilter, tags: [] };
    this.searchOrder = 0;
    this.suppressSortDrivenSearch = true;
    this.clearSort();
    try {
      await this.onSearch();
    } finally {
      this.isReloadLoading = false;
      setTimeout(() => {
        this.suppressSortDrivenSearch = false;
      }, 0);
    }
  }

  async onSearch(fetchSearchCounter = true): Promise<void> {
    this.resetScrollData();
    this.infinityScroll?.scrollTop();
    await this.loadContactList(fetchSearchCounter);
  }

  clearSort(): void {
    if (!this.sort) {
      return;
    }
    this.sort.sort({ id: '', start: 'asc', disableClear: false });
    this.sort.active = '';
    this.sort.direction = '';
  }

  async changeOrder(): Promise<void> {
    this.filter.orderDirection =
      this.filter.orderDirection === OrderDirection.Ascending
        ? OrderDirection.Descending
        : OrderDirection.Ascending;
    await this.onSearch();
  }

  public async searchOrderChange($event: any): Promise<void> {
    this.searchOrder = $event as ContactsSearchOrder;
    this.filter.searchOrder = this.searchOrder;
    this.filter.orderDirection = OrderDirection.Ascending;
    await this.onSearch();
  }

  async sortData(sort: Sort): Promise<void> {
    if (this.suppressSortDrivenSearch) {
      return;
    }
    this.filter.searchOrder = sort.direction
      ? this.formatContactsSearchOrders.transform(sort.active)
      : ContactsSearchOrder.OrderByContactId;

    this.filter.orderDirection =
      sort.direction === 'desc'
        ? OrderDirection.Descending
        : OrderDirection.Ascending;

    await this.onSearch();
  }

  async exportExcel(): Promise<void> {
    if (!this.mailAccountId) return;
    this.isExporting = true;
    try {
      const fileResponse = await this.contactClient.exportExcel({
        mailAccountId: this.mailAccountId,
        ids: this.selection.hasValue()
          ? (this.selection.selected.map(el => el.contactId) as number[])
          : [],
      });
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isExporting = false;
    }
  }

  async exportVcCards(): Promise<void> {
    if (!this.mailAccountId) return;
    this.isExportingVcCards = true;
    try {
      const fileResponse = await this.contactClient.exportVcCards({
        mailAccountId: this.mailAccountId,
        ids: this.selection.hasValue()
          ? (this.selection.selected.map(el => el.contactId) as number[])
          : [],
      });
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (error: any) {
      const isEmpty = error.Code === ErrorCode.EntityDoesNotExists;
      if (isEmpty) {
        this.matchError.openErrorDialog('noContacts', 'noExportContacts');
        return;
      }
      this.matchError.errorHandler(error);
      this.matchError.logError(error);
    } finally {
      this.isExportingVcCards = false;
    }
  }

  async uploadContacts(): Promise<void> {
    this.drawerService.openDrawer(
      this.uploadContactsDrawer,
      undefined,
      'create-email'
    );
  }

  async retryData(): Promise<void> {
    await this.loadContactList(true);
    setTimeout(() => {
      this.infinityScroll?.reconnect();
    }, 1000);
  }

  loadTags = async (search: string | null = null): Promise<TagContract[]> => {
    if (!this.mailAccountId) return [];
    const response = await this.tagClient.searchTagsForSelect(
      this.mailAccountId,
      TagType.Contact,
      search
    );
    return response.data;
  };

  insertTag = async (tag: string, color: string): Promise<TagContract> => {
    const requestData = {
      mailAccountId: this.mailAccountId ?? 0,
      tag,
      color,
      type: TagType.Contact,
    };
    const response = await this.tagClient.insert(requestData);
    return { ...requestData, tagId: response.id };
  };

  saveTags = async (selectedTags: TagContract[]): Promise<void> => {
    const contactIds =
      this.taggingContacts
        ?.map(el => el.contactId)
        .filter((id): id is number => id != null) ?? [];

    await this.contactClient.saveTags({
      mailAccountId: this.mailAccountId,
      contactIds,
      tagIds: selectedTags.map(el => el.tagId),
    });
    this.onSearch();
  };

  get tagsInitial(): TagContract[] {
    const list = this.taggingContacts;
    if (!list?.length) return [];
    return list.length === 1 ? [...(list[0].tags ?? [])] : [];
  }

  openFiltersTag(): void {
    this.isOpenFilters = true;
    this.drawerService.openDrawer(
      this.filterTagsDrawer,
      () => {
        this.isOpenFilters = false;
        this.drawerService.closeDrawer();
      },
      'small'
    );
  }

  searchWithFilter = async (tags: TagContract[]): Promise<void> => {
    this.filter = {
      ...this.filter,
      tags,
    };
    this.isOpenFilters = false;
    this.drawerService.closeDrawer();
    await this.loadContactList(true);
  };

  ngOnDestroy(): void {
    this.useMobileViewSubscription?.unsubscribe();
  }
}
